import { ConfigService } from '@axo/config/ConfigService';
import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { Residence } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { Grid } from '@axo/ui-core/components/layout/item';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useBroker } from '../../../../../../hooks/useBroker';
import { formatSpacedNumber } from '../../../../../../utils/format';
import { HousingFormGroupData } from '../../../../FormData.fi.types';
import { gridColumns } from '../../../config/layouts/grid';
import { ApartmentTypeField, HousingTypeField } from '../../../fields';
import styles from '../../../loan.module.scss';
import {
  livingCostMonthlyFieldOptions,
  ownerAssociationCostMonthlyFieldOptions,
  rentCostMonthlyFieldOptions,
} from './config';
import { HousingDebtFormGroup } from './HousingDebtFormGroup';

export const HousingFormGroup = () => {
  const { t } = useTranslation();

  const broker = useBroker();
  const { hasApartmentType } = ConfigService.get('product-unsecured-loan')
    .fields[broker];

  const { watch } = useFormContext();

  const [maritalStatus, housingType] = watch([
    'LoanApplication/MaritalStatus',
    'LoanApplication/HousingType',
  ]);

  const {
    askHousingDebt,
    askOwnerAssociationCostMonthly,
    askRentCostMonthly,
    askApartmentType,
  } = useMemo(() => {
    const askRentCostMonthly = [
      Residence.Rental,
      Residence.Cohabitant,
      Residence.CompanyOwnedFlat,
      Residence.PartnersHouse,
      Residence.WithParents,
    ].includes(housingType);

    const askOwnerAssociationCostMonthly = [
      Residence.OwnHouse,
      Residence.OwnApartment,
    ].includes(housingType);

    const askHousingDebt = [
      Residence.OwnHouse,
      Residence.OwnApartment,
      Residence.Cohabitant,
    ].includes(housingType);

    const askApartmentType = hasApartmentType && !!housingType;

    return {
      askRentCostMonthly,
      askOwnerAssociationCostMonthly,
      askHousingDebt,
      askApartmentType,
    };
  }, [housingType, hasApartmentType]);

  return (
    <>
      <div className={styles.inputGroup} data-grey={true}>
        <Grid columns={gridColumns}>
          <HousingTypeField />
        </Grid>

        <Grid columns={gridColumns}>
          {askApartmentType && <ApartmentTypeField />}

          {askRentCostMonthly && (
            <NumberFieldUI<HousingFormGroupData>
              name="LoanApplication/RentCostMonthly"
              label={t('RentCostMonthly.label', { context: maritalStatus })}
              data-testid="rent-cost-monthly-input"
              format={formatSpacedNumber}
              suffix={t('suffix.currency')}
              {...rentCostMonthlyFieldOptions}
            />
          )}

          {askOwnerAssociationCostMonthly && (
            <NumberFieldUI<HousingFormGroupData>
              name="LoanApplication/OwnerAssociationCostMonthly"
              label={t('OwnerAssociationCostMonthly.label')}
              data-testid="owner-association-cost-monthly-input"
              format={formatSpacedNumber}
              suffix={t('suffix.currency')}
              {...ownerAssociationCostMonthlyFieldOptions}
            />
          )}

          {askHousingDebt && <HousingDebtFormGroup />}
        </Grid>
      </div>

      <Grid columns={gridColumns}>
        <NumberFieldUI<HousingFormGroupData>
          name="LoanApplication/LivingCostMonthly"
          label={t('LivingCostMonthly.label')}
          data-testid="living-cost-monthly-input"
          format={formatSpacedNumber}
          suffix={t('suffix.currency')}
          {...livingCostMonthlyFieldOptions}
        />
      </Grid>
    </>
  );
};
